/**
 * Command encoder, mirroring the decoder in `crates/sim/src/command.rs`.
 *
 * Commands accumulate here during a turn and are flushed as one packed buffer.
 * The byte layout is the contract between this file and the Rust decoder; if
 * they disagree, clients desync, so both sides are covered by tests.
 */

import {
  CMD_ATTACK,
  CMD_ATTACK_MOVE,
  CMD_CANCEL,
  CMD_HARVEST,
  CMD_MOVE,
  CMD_PLACE,
  CMD_PRODUCE,
  CMD_STOP,
  FIXED_ONE,
} from './constants.js';

/** Converts world tile coordinates into the simulation's 16.16 fixed point. */
export function toFixed(v: number): number {
  return Math.round(v * FIXED_ONE) | 0;
}

export class CommandBuffer {
  private bytes: number[] = [];

  get length(): number {
    return this.bytes.length;
  }

  clear(): void {
    this.bytes.length = 0;
  }

  private u8(v: number): void {
    this.bytes.push(v & 0xff);
  }

  private u16(v: number): void {
    this.bytes.push(v & 0xff, (v >>> 8) & 0xff);
  }

  private i32(v: number): void {
    this.bytes.push(v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff);
  }

  private entities(ids: number[] | Uint32Array): void {
    this.u16(ids.length);
    for (const id of ids) this.i32(id);
  }

  move(player: number, ids: number[] | Uint32Array, x: number, y: number, attackMove = false): void {
    this.u8(attackMove ? CMD_ATTACK_MOVE : CMD_MOVE);
    this.u8(player);
    this.entities(ids);
    this.i32(toFixed(x));
    this.i32(toFixed(y));
  }

  attack(player: number, ids: number[] | Uint32Array, target: number): void {
    this.u8(CMD_ATTACK);
    this.u8(player);
    this.entities(ids);
    this.i32(target);
  }

  stop(player: number, ids: number[] | Uint32Array): void {
    this.u8(CMD_STOP);
    this.u8(player);
    this.entities(ids);
  }

  harvest(player: number, ids: number[] | Uint32Array, tx: number, ty: number): void {
    this.u8(CMD_HARVEST);
    this.u8(player);
    this.entities(ids);
    this.u16(tx);
    this.u16(ty);
  }

  produce(player: number, kind: number): void {
    this.u8(CMD_PRODUCE);
    this.u8(player);
    this.u8(kind);
  }

  cancel(player: number, kind: number): void {
    this.u8(CMD_CANCEL);
    this.u8(player);
    this.u8(kind);
  }

  place(player: number, kind: number, tx: number, ty: number): void {
    this.u8(CMD_PLACE);
    this.u8(player);
    this.u8(kind);
    this.u16(tx);
    this.u16(ty);
  }

  /** Returns the packed buffer and resets for the next turn. */
  flush(): Uint8Array {
    const out = new Uint8Array(this.bytes);
    this.bytes.length = 0;
    return out;
  }
}
